// Package imports:
import React from 'react';
// Component imports:
import Form from 'react-bootstrap/Form';
import Table from 'react-bootstrap/Table';
import Alert from 'react-bootstrap/Alert';
// Service imports:
import { findMostRecentHistoryEntry, isParameterBoolean, isParameterPercentNumber, isParameterString } from '../../services/alertUtils';
import { formatDate } from '../../services/utils';
// Type imports:
import { IAlertConfig, IAlertConfigParameter, IAlertConfigParameterChange } from '../../types/api/AlertTypes';

interface IOwnProps {
    alertConfiguration: IAlertConfig,
    parameterConfigurationChanges: IAlertConfigParameterChange[],
    errorMessages: string[],
    handleConfigParameterChange: (
        alertId: number,
        paramId: string,
        newValue: number | string | null,
        currentValue: number | string | null
    ) => void
}
type Props = IOwnProps;

const addSpacesToUsername = (username: string) => {
    return username.replace('@', '@\u200A').replaceAll('.', '.\u200A');
}


const displayValue = (
    configParameter: IAlertConfigParameter, 
    valueToDisplay: any, 
    handleConfigParameterChange: any, 
    alertConfiguration: IAlertConfig, 
    currentOrDefaultValue: any
) => {
    return (
        <tr key={configParameter.id}>
            <td className='text-left'>{configParameter.description}</td>
            <td className='text-center'>
                {
                    (isParameterBoolean(configParameter.id)) // if
                    ? (
                        <Form.Control
                            className='BoolParameterChangeInput'
                            type='checkbox'
                            defaultChecked={(valueToDisplay === 1)}
                            onChange={e => {
                                const value = ((e.target as HTMLInputElement).checked) ? 1 : 0;
                                handleConfigParameterChange(alertConfiguration.id, configParameter.id, value, currentOrDefaultValue.value)
                            }}
                        />
                    )
                    : (isParameterPercentNumber(configParameter.id)) // esle if
                    ? (
                        <Form.Control
                            className='WideParameterChangeInput'
                            type='number'
                            step={0.001}
                            defaultValue={valueToDisplay || undefined}
                            onChange={e => {
                                const { valueAsNumber } = e.target as HTMLInputElement;
                                const value = (isNaN(valueAsNumber)) ? null : valueAsNumber;
                                handleConfigParameterChange(alertConfiguration.id, configParameter.id, value, currentOrDefaultValue.value);
                            }}
                        />
                    )
                    : (isParameterString(configParameter.id)) // else if
                    ? (
                        <Form.Control
                            className='WideParameterChangeInput'
                            defaultValue={valueToDisplay || undefined}
                            onChange={e => {
                                const { value } = e.target;
                                handleConfigParameterChange(alertConfiguration.id, configParameter.id, value, currentOrDefaultValue.value_text);
                            }}
                        />
                    ) : ( // eles
                        <Form.Control
                            className='WideParameterChangeInput'
                            type='number'
                            step={1}
                            defaultValue={valueToDisplay || undefined}
                            onChange={e => {
                                const { valueAsNumber } = e.target as HTMLInputElement;
                                const value = (isNaN(valueAsNumber)) ? null : valueAsNumber;
                                handleConfigParameterChange(alertConfiguration.id, configParameter.id, value, currentOrDefaultValue.value);
                            }}
                        />
                    )
                }
            </td>
            <td>{addSpacesToUsername(currentOrDefaultValue.username)}</td>
            <td>{formatDate(currentOrDefaultValue.lastChanged)}</td>
        </tr>
    )
}

const setConfigParameterControler = (
    configParameter: IAlertConfigParameter, 
    handleConfigParameterChange: any, 
    alertConfiguration: IAlertConfig, 
    parameterConfigurationChanges: IAlertConfigParameterChange[]
) => {
    const mostRecentActiveParameterHistoryEntry = findMostRecentHistoryEntry(configParameter);
    const currentOrDefaultValue = (mostRecentActiveParameterHistoryEntry === undefined)
        ? {
            value: configParameter.default_value,
            value_text: configParameter.default_value_text,
            username: 'Default',
            lastChanged: null
        } : {
            value: mostRecentActiveParameterHistoryEntry.value,
            value_text: mostRecentActiveParameterHistoryEntry.value_text,
            username: mostRecentActiveParameterHistoryEntry.username,
            lastChanged: mostRecentActiveParameterHistoryEntry.created_timestamp || null
        };

    // Find if this config has a change.
    const parameterChange = parameterConfigurationChanges.find(change => (
        change.alertId === alertConfiguration.id
        && change.paramId === configParameter.id
    ));
    // Set the Display Value
    let valueToDisplay;
    if (parameterChange !== undefined) {
        valueToDisplay = parameterChange.newValue;
    } else if (isParameterString(configParameter.id)) {
        valueToDisplay = currentOrDefaultValue.value_text;
    } else {
        valueToDisplay = currentOrDefaultValue.value;
    }

    return displayValue(configParameter, valueToDisplay, handleConfigParameterChange, alertConfiguration, currentOrDefaultValue);
}


/**
 * Alert Table in Alert Configuration.
 * **This is in the PopUp!**
 * - Subcomponent of ConfigurationModal.tsx
 */
const ConfigurationModalAlertTable: React.FC<Props> = ({
    alertConfiguration,
    parameterConfigurationChanges,
    errorMessages,
    handleConfigParameterChange
}) => {
    const displayRows = () => {
        const { alertConfigParameters } = alertConfiguration;
        const sortedAlertConfigParameters = [...alertConfigParameters].sort((param1, param2) => {
            if (param1.id < param2.id) return -1;
            if (param1.id > param2.id) return 1;
            return 0;
        });

        return sortedAlertConfigParameters.map((configParameter: IAlertConfigParameter) => {
            return setConfigParameterControler(
                configParameter, 
                handleConfigParameterChange, 
                alertConfiguration, 
                parameterConfigurationChanges
            );
        })
    }

    const displayErrorAlert = () => {
        if (errorMessages.length > 0) {
            return (
                <Alert variant='danger'>
                    <p>
                        {errorMessages.map(msg => (
                            <>
                                {msg}
                                <br/>
                            </>
                        ))}
                    </p>
                </Alert>
            )
        }
        return null;
    }

    return (
        <Form>
            <Table className='CompressedTable'>
                <thead>
                    <tr>
                        <th className='text-left'>Parameter</th>
                        <th className='text-center'>Value</th>
                        <th>Last changed by</th>
                        <th>Last changed</th>
                    </tr>
                </thead>
                <tbody>
                    {displayRows()}
                </tbody>
            </Table>
            {displayErrorAlert()}
        </Form>
    );
}

export default ConfigurationModalAlertTable;